/**
 * B2B bulk billing API client.
 *
 * Follows the house pattern (see `marketingApi.ts`): import the shared axios
 * instance so every call inherits the auth header, the request id, the 401
 * logout and the global error toast. No react-query, no second client.
 *
 * Unlike `adminApi`, these functions return the UNWRAPPED payload rather than
 * the `{ success, data }` envelope, so callers get a typed object instead of
 * writing `unwrap(res) as Something` at every call site. `unwrap` itself is the
 * shared one - it is the single source of truth and is not reimplemented here.
 *
 * ── A note on types ────────────────────────────────────────────────────────
 * `features/b2b/types.ts` was written for the mock store, and its record shapes
 * are not the wire format:
 *
 *   B2bClient  carries `rateCard` inline and uses `type`/`pocName`/`billingCycle`;
 *              the API serves rates from their own endpoint (a client list must
 *              not carry 12 rate rows per row) and uses snake_case columns.
 *   B2bRoom    has a per-cycle `status`; the server deliberately stores none -
 *              a room is "done" when it has a saved order in the open cycle, so
 *              nothing has to be reset across 200 rows at invoice time.
 *
 * So the shared VOCABULARY is reused verbatim (`B2bClientType`,
 * `BillingCycleLength`, `EntryType`, `CycleStatus`, `B2bRateCard`, and the
 * order/line shapes, which do match), and the records that genuinely differ are
 * described here as what the server actually returns. Reconciling the mock
 * store's types with these is part of wiring the screens.
 */
import api, { unwrap } from './api'
import type {
  B2bClientType,
  B2bOrderItem,
  B2bRateCard,
  BillingCycleLength,
  CycleStatus,
  EntryType,
} from '../features/b2b/types'

export type B2bClientStatus = 'draft' | 'active' | 'paused'

/** A client row as the API returns it. */
export interface B2bClientRecord {
  id: string
  name: string
  client_type: B2bClientType
  status: B2bClientStatus
  address: string
  poc_name: string | null
  poc_phone: string | null
  billing_cycle_length: BillingCycleLength
  billing_cycle_anchor: number | null
  room_pattern: string | null
  room_mode: 'ranges' | 'custom' | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface B2bRoomRecord {
  id: string
  room_number: string
  block: string | null
  floor: number | null
  is_active: boolean
}

/** Open-cycle rollup shown against a client in the master table. */
export interface B2bOpenCycleBrief {
  id: string
  label: string
  starts_on: string
  total_amount: number
  rooms_billed: number
}

export interface B2bClientListItem extends B2bClientRecord {
  room_count: number
  open_cycle: B2bOpenCycleBrief | null
}

export interface B2bClientListResponse {
  clients: B2bClientListItem[]
  page: number
  limit: number
  total: number
}

export interface B2bClientListParams {
  search?: string
  client_type?: B2bClientType
  status?: B2bClientStatus
  page?: number
  limit?: number
  sort?: 'name' | 'created_at' | 'updated_at'
  order?: 'asc' | 'desc'
}

/**
 * Room generation input. Exactly ONE of `pattern`, `ranges` or `rooms` - the
 * server rejects a payload carrying more than one rather than guessing.
 * The server expands it; a client-expanded list is never trusted.
 */
export interface B2bRoomRange {
  block?: string
  /** Floor prefix: floor "1" + room 4 → "104". Blank means no prefix. */
  floor?: string | number
  from: string | number
  to: string | number
}

export interface B2bRoomsInput {
  mode?: 'ranges' | 'custom'
  /** "101-120, 201-220" */
  pattern?: string
  ranges?: B2bRoomRange[]
  rooms?: string[]
  block?: string
}

export interface B2bClientInput {
  name: string
  client_type: B2bClientType
  address: string
  status?: B2bClientStatus
  poc_name?: string | null
  poc_phone?: string | null
  billing_cycle_length?: BillingCycleLength
  billing_cycle_anchor?: number | null
  notes?: string | null
  rooms?: B2bRoomsInput
}

export interface B2bServiceCatalogueEntry {
  slug: string
  label: string
  entryType: EntryType
  /** Tag prefix printed on every piece label - WI, DC, SC (FRD §7). */
  code: string
  items: Array<{ name: string; defaultPrice: number }>
  defaultPrice?: number
}

export interface B2bCycleRecord {
  id: string
  label: string
  starts_on: string
  ends_on: string | null
  status: CycleStatus
}

/** One saved service on one room. Line items match `B2bOrderItem` exactly. */
export interface B2bOrderRecord {
  id: string
  roomId: string
  cycleId: string
  serviceSlug: string
  entryType: EntryType
  amount: number
  /** Physical pieces to tag. Not derivable from a weight - the operator enters it. */
  pieceCount: number
  printedAt: string | null
  createdAt: string
  items: B2bOrderItem[]
}

export interface B2bServiceSubtotal {
  service_slug: string
  label: string
  code: string
  entry_type: EntryType
  amount: number
  order_count: number
  piece_count: number
}

export interface B2bCycleSummary {
  cycle_id: string
  total_amount: number
  rooms_total: number
  rooms_billed: number
  rooms_pending: number
  order_count: number
  piece_count: number
  by_service: B2bServiceSubtotal[]
  by_entry_type: { weight: number; items: number }
}

/** The billing console's entire boot payload - one request, not N+1. */
export interface B2bCycleResponse {
  client: Pick<
    B2bClientRecord,
    'id' | 'name' | 'client_type' | 'status' | 'address' | 'poc_name' | 'poc_phone' | 'billing_cycle_length'
  >
  rate_card: B2bRateCard
  catalogue: B2bServiceCatalogueEntry[]
  rooms: B2bRoomRecord[]
  cycle: B2bCycleRecord
  orders: B2bOrderRecord[]
  summary: B2bCycleSummary
}

/** A room's state after a write, so the console reconciles without a second call. */
export interface B2bRoomState {
  roomId: string
  orders: B2bOrderRecord[]
  total: number
  pieceCount: number
}

export interface B2bWriteResponse {
  cycle_id: string
  rooms: B2bRoomState[]
  summary: B2bCycleSummary
}

/** What a print run stamped, so the grid's "Tags printed" marker matches the roll. */
export interface B2bPrintedResponse {
  printed_at: string
  marked: number
  /** Ids the caller sent that no longer exist - a room deleted mid-print. */
  missing: string[]
  orders: Array<{ id: string; roomId: string; cycleId: string; serviceSlug: string; printedAt: string }>
}

/** A consolidated invoice row as the API returns it. */
export interface B2bInvoiceRecord {
  id: string
  client_id: string
  cycle_id: string
  invoice_number: string
  total_amount: number
  rooms_billed: number
  payment_status: 'unpaid' | 'partly_paid' | 'paid'
  amount_paid: number
  paid_at: string | null
  generated_at: string
}

export interface B2bInvoiceListItem extends B2bInvoiceRecord {
  client_name: string
  cycle_label: string | null
  outstanding: number
}

/** One billed room-service on the invoice's supporting breakup. */
export interface B2bInvoiceLine {
  room_number: string
  block: string | null
  service_slug: string
  service_label: string
  amount: number
  piece_count: number
}

export interface B2bInvoiceDetail {
  invoice: B2bInvoiceRecord
  client: { id: string; name: string; address: string; poc_name: string | null; poc_phone: string | null } | null
  cycle: { id: string; label: string; starts_on: string; ends_on: string | null; status: CycleStatus } | null
  lines: B2bInvoiceLine[]
}

/**
 * `created: false` means the cycle had already been invoiced and the EXISTING
 * invoice came back - the double-click answer, not an error.
 */
export interface B2bGenerateInvoiceResponse {
  invoice: B2bInvoiceRecord
  created: boolean
  next_cycle: { id: string; label: string; starts_on: string; status: CycleStatus } | null
}

/** One service being saved onto a room. Numbers may be strings - the console holds them that way. */
export interface B2bOrderDraft {
  serviceSlug: string
  /** Weight services only. */
  weightKg?: string | number
  ratePerKg?: string | number
  pieces?: string | number
  /** Item services only. */
  items?: Array<{ itemName: string; quantity: string | number; unitPrice: string | number }>
}

const get = async <T,>(url: string, params?: unknown): Promise<T> => {
  const response = await api.get(url, params ? { params } : undefined)
  return unwrap<T>(response.data)
}

export const b2bApi = {
  // ── Clients ──────────────────────────────────────────────────────
  listClients: (params?: B2bClientListParams) =>
    get<B2bClientListResponse>('/admin/b2b/clients', params),

  createClient: async (payload: B2bClientInput) => {
    const response = await api.post('/admin/b2b/clients', payload)
    return unwrap<{ client: B2bClientRecord; rooms_created: number }>(response.data)
  },

  getClient: (clientId: string) =>
    get<{
      client: B2bClientRecord
      rooms: B2bRoomRecord[]
      room_count: number
      open_cycle: (B2bOpenCycleBrief & { ends_on: string | null; status: CycleStatus; order_count: number }) | null
      /** What the client still owes across every unsettled invoice. */
      outstanding: { amount: number; invoice_count: number }
    }>(`/admin/b2b/clients/${clientId}`),

  updateClient: async (clientId: string, payload: Partial<B2bClientInput>) => {
    const response = await api.patch(`/admin/b2b/clients/${clientId}`, payload)
    return unwrap<B2bClientRecord>(response.data)
  },

  // ── Rooms ────────────────────────────────────────────────────────
  addRooms: async (clientId: string, payload: B2bRoomsInput) => {
    const response = await api.post(`/admin/b2b/clients/${clientId}/rooms`, payload)
    return unwrap<{ rooms_created: number; total_rooms: number }>(response.data)
  },

  updateRoom: async (
    roomId: string,
    payload: { room_number?: string; block?: string | null; floor?: number | null; is_active?: boolean },
  ) => {
    const response = await api.patch(`/admin/b2b/rooms/${roomId}`, payload)
    return unwrap<B2bRoomRecord & { client_id: string }>(response.data)
  },

  // ── Rate card ────────────────────────────────────────────────────
  getRates: (clientId: string) =>
    get<{ client_id: string; rate_card: B2bRateCard; catalogue: B2bServiceCatalogueEntry[] }>(
      `/admin/b2b/clients/${clientId}/rates`,
    ),

  /** Full replace. Overriding a line during entry never writes back here (FRD §8). */
  replaceRates: async (clientId: string, rateCard: B2bRateCard) => {
    const response = await api.put(`/admin/b2b/clients/${clientId}/rates`, rateCard)
    return unwrap<{ client_id: string; rate_card: B2bRateCard }>(response.data)
  },

  // ── Billing ──────────────────────────────────────────────────────
  /** The console's boot payload. Opens the client's first cycle if there is none. */
  getOpenCycle: (clientId: string) => get<B2bCycleResponse>(`/admin/b2b/clients/${clientId}/cycle`),

  /**
   * Replace one room's services. Pass the `cycleId` the console is DISPLAYING:
   * if that cycle was invoiced while the operator was typing, the save is
   * refused with 409 rather than quietly filed against the next period.
   */
  saveRoomOrders: async (roomId: string, orders: B2bOrderDraft[], cycleId?: string) => {
    const response = await api.put(`/admin/b2b/rooms/${roomId}/orders`, { cycleId, orders })
    return unwrap<B2bWriteResponse>(response.data)
  },

  /** Save Changes: every dirty room in one atomic request. */
  saveCycleOrders: async (cycleId: string, rooms: Array<{ roomId: string; orders: B2bOrderDraft[] }>) => {
    const response = await api.put(`/admin/b2b/cycles/${cycleId}/orders`, { rooms })
    return unwrap<B2bWriteResponse & { rooms_saved: number }>(response.data)
  },

  /**
   * Record that a tag run went to the printer (FRD §7).
   *
   * Sent AFTER the print window has the job, never before: a stamp written for
   * a print the operator then cancelled would suppress the reprint warning on
   * tags that were never made.
   */
  markTagsPrinted: async (orderIds: string[]) => {
    const response = await api.post('/admin/b2b/orders/printed', { orderIds })
    return unwrap<B2bPrintedResponse>(response.data)
  },

  // ── Consolidated invoices ────────────────────────────────────────
  /**
   * Close the cycle, create the invoice, open the next - one server-side
   * transaction. Pass the cycle the screen is DISPLAYING: a second click then
   * resolves to that cycle's existing invoice instead of the fresh empty one
   * generation just opened, which is what makes the button safe to double-tap.
   */
  generateInvoice: async (clientId: string, cycleId?: string) => {
    const response = await api.post(`/admin/b2b/clients/${clientId}/invoice`, { cycleId })
    return unwrap<B2bGenerateInvoiceResponse>(response.data)
  },

  listInvoices: (params?: { client_id?: string; payment_status?: string; page?: number; limit?: number }) =>
    get<{ invoices: B2bInvoiceListItem[]; page: number; limit: number; total: number }>(
      '/admin/b2b/invoices',
      params,
    ),

  getInvoice: (invoiceId: string) => get<B2bInvoiceDetail>(`/admin/b2b/invoices/${invoiceId}`),

  /**
   * Record what a client has paid. Status and amount are reconciled server-side
   * - omit the amount when marking Paid and it is filled in with the total.
   */
  updateInvoicePayment: async (
    invoiceId: string,
    payload: { payment_status: B2bInvoiceRecord['payment_status']; amount_paid?: string | number },
  ) => {
    const response = await api.patch(`/admin/b2b/invoices/${invoiceId}/payment`, payload)
    return unwrap<{ invoice: B2bInvoiceRecord; outstanding: number }>(response.data)
  },

  /**
   * The rendered document, as bytes.
   *
   * Deliberately a Blob rather than a URL the browser fetches itself: the PDF
   * route needs the Authorization header the shared axios instance carries, and
   * an <iframe src="/admin/b2b/invoices/../pdf"> would be an unauthenticated
   * request. The caller turns this into an object URL for the frame, the
   * download and the print job - one fetch, three uses, and the operator sees
   * the exact bytes the client receives.
   */
  getInvoicePdf: async (invoiceId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/admin/b2b/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
    // The server names the file for the client and the period, which is what an
    // accountant looks for a year later. Take its name rather than inventing a
    // second convention here.
    const disposition = String(response.headers?.['content-disposition'] || '')
    const match = disposition.match(/filename="?([^";]+)"?/i)
    return {
      blob: response.data as Blob,
      filename: match ? match[1] : `Rapidry-Invoice-${invoiceId}.pdf`,
    }
  },

  deleteOrder: async (orderId: string) => {
    const response = await api.delete(`/admin/b2b/orders/${orderId}`)
    return unwrap<B2bWriteResponse & { deleted: boolean }>(response.data)
  },
}

export default b2bApi
